import CustomDrawer from '@/components/atoms/CustomDrawer';
import LogUserCard from './LogUserCard';
import LogDetailsCard from './LogDetailsCard';
import DeleteHeader from './DeleteHeader';
import ChangesTable from './ChangesTable';
import { usePublicState } from '@/store/slices/publicSlice';
import { useGetConfigLogByIdQuery } from '@/store/services/configLogsService';
import { useTranslation } from 'react-i18next';

export interface CreatedBy {
  image: string | null;
  name: string;
  email: string;
  role: string | null;
}

export interface Action {
  name: string;
  code: string;
}

export interface ActivityDetails {
  functionName: string;
  placement: string;
  date: string;
  action: Action;
  status: 'SUCCESS' | 'FAILED' | string;
  errorMessage: string | null;
}

export interface ChangedValue {
  attribute: string;
  oldValue: string | null;
  newValue: string | null;
  type: string;
  document: string | null;
}

export interface ActivityLog {
  id: string;
  createdBy: CreatedBy;
  details: ActivityDetails;
  changedValues: ChangedValue[];
}

export default function AdminLogDetailsPopup({
  listOfIds,
}: {
  listOfIds: string[];
}) {
  const { t } = useTranslation();
  const { modalId } = usePublicState();
  const isDrawerOpen = Boolean(modalId && listOfIds?.includes(modalId));

  const {
    data: logDetails,
    isLoading,
    isFetching,
  } = useGetConfigLogByIdQuery(modalId as string, {
    skip: !isDrawerOpen || !modalId,
  });

  return (
    <CustomDrawer title={t('configLogs.logDetails')} listOfIds={listOfIds}>
      <div className="space-y-6">
        <LogUserCard
          email={logDetails?.payload?.createdBy?.email}
          name={logDetails?.payload?.createdBy?.name}
          role={logDetails?.payload?.createdBy?.role || '-'}
          avatarUrl={logDetails?.payload?.createdBy?.image as string}
        />

        <LogDetailsCard
          isLoading={isLoading || isFetching}
          date={logDetails?.payload?.details?.date || new Date()}
          functionType={logDetails?.payload?.details?.functionName as string}
          placement={logDetails?.payload?.details?.placement as string}
          action={logDetails?.payload?.details?.action as Action}
        />

        {logDetails?.payload?.details?.action?.code === 'DELETE' && (
          <DeleteHeader
            isLoading={isLoading || isFetching}
            value={`${logDetails?.payload?.details?.action?.name} ${logDetails?.payload?.details?.placement}`}
          />
        )}

        <ChangesTable
          title={
            logDetails?.payload?.details?.action?.code === 'DELETE'
              ? t('configLogs.deletedItem')
              : ''
          }
          response={logDetails?.payload?.changedValues}
          actionCode={logDetails?.payload?.details?.action?.code}
        />
      </div>
    </CustomDrawer>
  );
}
